import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselDots } from "@/components/ui/carousel";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import OptimizedImage from "@/components/OptimizedImage";
import LoadingSkeleton, { BookGridSkeleton, ReviewsSkeleton, HeroSkeleton } from "@/components/LoadingSkeleton";



//slides 
const slides = [
  "/images/slide1.png",
  "/images/slide2.png",
  "/images/slide3.png",
  "/images/slide4.png",
];

// First, update the book type to include a raw price
interface Book {
  title: { en: string; fr: string; ar: string };
  description: { en: string; fr: string; ar: string };
  price: string;
  rawPrice: number;  // Add this field
  image: string;
  language: string;
}

// Update the books data to include rawPrice
const books: Book[] = [
  {
      title: { en: "Healing from Within", fr: "شفاء ذاتي", ar: "شفاء ذاتي" },
      description: { en: "A transformative guide to inner peace.", fr: "Un guide transformateur vers la paix intérieure.", ar: "دليل تحويلي للسلام الداخلي" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/shifaathati_cover.png",
      language: "Arabic/French"
  },
  {
      title: { en: "Loupo ", fr: "Loupo", ar: "Loupo" },
      description: { en: "An inspirational story for children and adults about dreaming big.", fr: "Une histoire inspirante pour enfants et adultes sur le rêve d'un poisson d'élevage.", ar: "قصة ملهمة للأطفال والكبار عن حلم سمكة مزارع بالوصول إلى المحيط." },
      price: "19 DT",
      rawPrice: 19,
      image: "/images/bookcover8.jpeg",
      language: "French"
  },
  {
      title: { en: "The Way to Myself", fr: "Le chemin vers moi-même", ar: "الطريق الى نفسي" },
      description: { en: "A novel about self-discovery set in Positano, Italy", fr: "Un roman sur la découverte de soi à Positano, Italie", ar: "رواية عن اكتشاف الذات في بوزيتانو، إيطاليا" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/tariqi_ila_nafsi.png",
      language: "French/Arabic"
  },
  {
      title: { en: "Angel Stories", fr: "Histoires d'anges", ar: "قصص الملائكة" },
      description: { en: "A novel about human life from angels' perspective", fr: "Un roman sur la vie humaine du point de vue des anges", ar: "رواية عن الحياة البشرية من منظور الملائكة" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/histoires_danges.png",
      language: "French"
  },
  {
      title: { en: "Cultivate Joy", fr: "Cultivez la Joie", ar: "ازرع الفرح" },
      description: { en: "A workbook with 22 exercises for personal growth", fr: "Un cahier d'exercices avec 22 exercices pour le développement personnel", ar: "دفتر تمارين مع 22 تمرينًا للتطور الشخصي" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/path-to-joy-book.png",
      language: "French"
  },
  {
      title: { en: "Listen to Your Emotions", fr: "Écoutez vos Émotions", ar: "استمع إلى مشاعرك" },
      description: { en: "Your emotional GPS guide", fr: "Votre GPS émotionnel", ar: "دليلك العاطفي" },
      price: "35 DT",
      rawPrice: 35,
      image: "/images/path-to-emotions-book.png",
      language: "French"
  },
  {
      title: { en: "3 Clés pour l'éducation au 21e siècle", fr: "3 Clés pour l'éducation au 21e siècle", ar: "ثلاثة مفاتيح للتعليم في القرن الحادي والعشرين" },
      description: { en: "A guide on creativity, emotional intelligence, and nutrition in education.", fr: "Un guide sur la créativité, l'intelligence émotionnelle et l'alimentation dans l'éducation.", ar: "دليل حول الإبداع، الذكاء العاطفي والتغذية في التعليم" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/bookcover5.jpeg",
      language: "French"
  },
  {
      title: { en: "Nous sommes comme les vagues", fr: "Nous sommes comme les vagues", ar: "نحن مثل الأمواج" },
      description: { en: "A novel of personal growth and rediscovering life's meaning.", fr: "Un roman sur le développement personnel et la redécouverte du sens de la vie.", ar: "رواية عن التطور الشخصي وإعادة اكتشاف معنى الحياة" },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/bookcover6.png",
      language: "French"
  },
  {
      title: { en: "Et toi, qu'est-ce que tu sais faire ?", fr: "Et toi, qu'est-ce que tu sais faire ?", ar: "وأنت، ماذا تعرف أن تفعل؟" },
      description: { en: "A book helping young people find their talents and career paths.", fr: "Un livre aidant les jeunes à découvrir leurs talents et choix de carrière.", ar: "كتاب يساعد الشباب على اكتشاف مواهبهم ومساراتهم المهنية." },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/bookcover7.png",
      language: "French"
  },
  {
      title: { en: "Mom I don't like this school", fr: "Maman je n'aime pas cette école", ar: "ماما، لا أحب هذه المدرسة" },
      description: { 
          en: "A deep dive into why children resist school, their lack of motivation, and the meaning of education.",
          fr: "Une exploration des raisons pour lesquelles les enfants résistent à l'école, leur manque de motivation et le sens de l'éducation.",
          ar: "استكشاف لأسباب رفض الأطفال للمدرسة، وقلة انتباههم في الفصل، وعدم إيمانهم بالدبلوم."
      },
      price: "25 DT",
      rawPrice: 25,
      image: "/images/bookcover8.png",
      language: "French"
  },
  {
      title: { en: "رحلة السمكة الشجاعة", fr: "رحلة السمكة الشجاعة", ar: "رحلة السمكة الشجاعة" },
      description: { en: "An inspirational story for children and adults about dreaming big.", fr: "Une histoire inspirante pour enfants et adultes sur le rêve d'un poisson d'élevage.", ar: "قصة ملهمة للأطفال والكبار عن حلم سمكة مزارع بالوصول إلى المحيط" },
      price: "19 DT",
      rawPrice: 19,
      image: "/images/bookcover9.jpeg",
      language: "Arabic"
  },
  {
      title: { en: "بذور السعادة و السلام الداخلي", fr: "بذور السعادة و السلام الداخلي", ar: "بذور السعادة و السلام الداخلي" },
      description: { en: "Seeds of Happiness and Inner Peace.", fr: "Les graines du bonheur et de la paix intérieure.", ar: "بذور السعادة و السلام الداخلي" },
      price: "28 DT",
      rawPrice: 28,
      image: "/images/bookcover10.jpeg",
      language: "Arabic"
  },
  {
    title: { 
        en: "From Academic Failure to Global Success", 
        fr: "De l'échec scolaire au succès mondial", 
        ar: "قصة شاب من الفشل الدراسي إلى العالمية" 
    },
    description: { 
        en: "An inspiring journey of transformation and success.", 
        fr: "Un parcours inspirant de transformation et de succès.", 
        ar: "رحلة ملهمة من التحول والنجاح" 
    },
    price: "20 DT",
    rawPrice: 20,
    image: "/images/my_dream.jpg",
    language: "Arabic"
  },
  {
    title: { 
        en: "Engineering the Future", 
        fr: "L'ingénierie de l'avenir", 
        ar: "هندسة المستقبل" 
    },
    description: { 
        en: "3 skills for achieving success - comprehensive learning, economic intelligence, and artificial intelligence.", 
        fr: "3 compétences pour réussir - apprentissage complet, intelligence économique et intelligence artificielle.", 
        ar: "3 مهارات لتحقيق النجاح - تعلم شامل، ذكاء اقتصادي، وذكاء اصطناعي" 
    },
    price: "25 DT",
    rawPrice: 25,
    image: "/images/handasat_al_mustaqbal.png",
    language: "Arabic"
  },
  {
    title: { 
        en: "Land of Stars", 
        fr: "Terre des étoiles", 
        ar: "ارض النجوم" 
    },
    description: { 
        en: "An inspiring story that takes you on a journey through the land of stars.", 
        fr: "Une histoire inspirante qui vous emmène dans un voyage à travers la terre des étoiles.", 
        ar: "قصة ملهمة تأخذك في رحلة عبر أرض النجوم" 
    },
    price: "29 DT",
    rawPrice: 29,
    image: "/images/ard_al_nujum.png",
    language: "Arabic"
  }
];

// Reviews data
const reviews = [
  { id: 1, image: "/images/review1.jpeg" },
  { id: 2, image: "/images/review2.jpeg" },
  { id: 3, image: "/images/review3.jpeg" }
];

const ChifaathatiWebsite = () => {
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const navigate = useNavigate();

  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Translation function
  const getTranslation = (translations: {en: string, fr: string, ar: string}) => {
    return translations[currentLanguage as keyof typeof translations] || translations.en;
  };

  const translations = {
    home: { en: "Home", fr: "Accueil", ar: "الرئيسية" },
    books: { en: "Books", fr: "Livres", ar: "الكتب" },
    about: { en: "About", fr: "À propos", ar: "عن الكاتبة" },
    contact: { en: "Contact", fr: "Contact", ar: "اتصل" },
    myBooks: { en: "My Books", fr: "Mes Livres", ar: "كتبي" },
    buyNow: { en: "Buy Your Copy Now", fr: "Achetez Votre Copie Maintenant", ar: "اشتر نسختك الان" },
    downloadFree: { en: "Download a free clip", fr: "Téléchargez un extrait gratuit", ar: "تحميل مقطع مجاني" },
    downloadFullPDF: { en: "Download Full Book PDF - FREE", fr: "Télécharger le PDF complet - GRATUIT", ar: "تحميل الكتاب كاملاً PDF - مجاناً" },
    freeBookOffer: { en: "Get the complete book for free!", fr: "Obtenez le livre complet gratuitement!", ar: "احصل على الكتاب كاملاً مجاناً!" },
    downloadNow: { en: "Download Now", fr: "Télécharger maintenant", ar: "تحميل الآن" },
    aboutAuthor: { en: "About the Author", fr: "À propos de l'auteure", ar: "تعرف على الكاتبة" },
    authorName: { en: "Sondess Khalifa", fr: "Sondess Khalifa", ar: "سندس خليفة" },
    authorTitle: { en: "Author & Professor", fr: "Auteure & Professeure", ar: "كاتبة و أستاذة جامعية" },
    authorBio: { 
      en: "Sondess Khalifa is a university professor and accomplished author with a diverse collection of books in both Arabic and French. Her passion for knowledge, science, and literature finds expression through her YouTube channel اقرأ خير, where she actively shares insights and engages with her audience.",
      fr: "Sondess Khalifa est professeure universitaire et auteure accomplie avec une collection diversifiée de livres en arabe et en français. Sa passion pour la connaissance, la science et la littérature s'exprime à travers sa chaîne YouTube اقرأ خير, où elle partage activement ses connaissances et interagit avec son public.",
      ar: "سندس خليفة أستاذة جامعية بالمعهد العالي للدراسات التجارية بسوسة (تونس) و كاتبة، في رصيدها العديد من الكتب باللغة العربية و الفرنسية، تشارك شغفها بالمعرفة و بالعلوم و الكتب على قناتها على اليوتيوب اقرأ خير."
    },
    businessCoaching: { en: "Business Coaching", fr: "Coaching d'affaires", ar: "business coaching : تعلم كيف تطلق مشروعك الخاص" },
    educationalContent: { en: "Educational Content Creator", fr: "Créatrice de Contenu Éducatif", ar: "صانعة محتوى تعليمي" },
    discoverPodcast: { en: "Discover Sakina's Podcast", fr: "Découvrez le Podcast de Sakina", ar: "اكتشف بودكاست سكينة" },
    needCoaching: { en: "Need coaching from Sondess Khalifa?", fr: "Besoin d'un coaching avec Sondess Khalifa ?", ar: "هل تحتاج إلى كوتشينغ مع سندس خليفة؟" },
    requestCoaching: { en: "Request Coaching", fr: "Demande de Coaching", ar: "طلب كوتشينغ" },
    reviews: { en: "Reviews of Readers", fr: "Avis des lecteurs", ar: "مراجعات القراء" },
    footerText: { en: "© 2025 Chifaathati. All rights reserved.", fr: "© 2025 Chifaathati. Tous droits réservés.", ar: "© 2025 شفاء ذاتي. جميع الحقوق محفوظة." },
    footerCredit: { 
      en: "Made by VeltoTech LLC", 
      fr: "Créé par VeltoTech LLC", 
      ar: "VeltoTech LLC صنع بواسطة" 
    },
    comingSoon: { en: "Coming Soon", fr: "Bientôt", ar: "قريبا" },
    brandName: { en: "Chifaathati", fr: "Chifaathati", ar: "شفاء ذاتي" }
  };

  // Functions for modal
  const openImageModal = (imageUrl: string) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  // Functions for book modal
  const openBookModal = (book: Book) => {
    setSelectedBook(book);
    setShowBookModal(true);
  };

  const closeBookModal = () => {
    setShowBookModal(false);
    setSelectedBook(null);
  };

  // Handle language change
  const changeLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    // Set page direction based on language
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  // Add these helper functions at the top of your component
  const extractPriceNumber = (priceString: string): number => {
    // Extract only digits, ignore currency and other characters
    const numericValue = priceString.match(/\d+/);
    return numericValue ? parseInt(numericValue[0], 10) : 0;
  };

  // Update the filtering and sorting logic
  const getFilteredAndSortedBooks = (books: Book[], filterType: string) => {
    return [...books] // Create a new array to avoid mutating original data
      .filter(book => {
        switch (filterType) {
          case 'all':
            return true;
          case 'arabic':
            return book.language.toLowerCase().includes('arabic');
          case 'french':
            return book.language.toLowerCase().includes('french');
          default:
            return true;
        }
      })
      .sort((a, b) => {
        // Only sort if a price-related filter is selected
        if (filterType === 'price-asc' || filterType === 'price-desc') {
          // Use raw numeric values for sorting
          return filterType === 'price-asc' 
            ? a.rawPrice - b.rawPrice 
            : b.rawPrice - a.rawPrice;
        }
        return 0;
      });
  };

  // Update the books section to use the new function
  const filteredBooks = getFilteredAndSortedBooks(books, filterType);

  useEffect(() => {
    // Set initial page direction for French (LTR)
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'fr';
  }, []);

  return (
    <>
      {/* SEO Components */}
      <SEO 
        title={`${getTranslation(translations.brandName)} - ${getTranslation(translations.authorName)}`}
        description={getTranslation({
          en: "Discover transformative books by Sondess Khalifa in Arabic and French. Personal development, education, and inspiring stories for readers of all ages.",
          fr: "Découvrez les livres transformateurs de Sondess Khalifa en arabe et en français. Développement personnel, éducation et histoires inspirantes.",
          ar: "اكتشف الكتب التحويلية لسندس خليفة باللغتين العربية والفرنسية. التطوير الشخصي والتعليم والقصص الملهمة."
        })}
        locale={currentLanguage === 'ar' ? 'ar_TN' : currentLanguage === 'en' ? 'en_US' : 'fr_FR'}
        alternateLocales={['fr_FR', 'en_US', 'ar_TN']}
      />
      
      <StructuredData type="website" currentLanguage={currentLanguage} />
      <StructuredData type="author" currentLanguage={currentLanguage} />
      <StructuredData type="organization" currentLanguage={currentLanguage} />

      <div className={`min-h-screen bg-natural-bg ${currentLanguage === 'ar' ? 'font-arabic' : ''}`}>
      {/* Fixed Navbar with improved alignment */}
      <nav className="sticky top-0 z-50 w-full bg-natural-text/90 backdrop-blur-sm shadow-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Left/Right side (depending on language direction) - Title */}
          <div className="flex items-center">
            <a href="#" className="text-2xl font-bold text-natural-bg">
              {getTranslation(translations.brandName)}
            </a>
          </div>
          
          {/* Center - Navigation Links */}
          <div className="hidden md:flex items-center justify-center flex-1">
            <div className="flex space-x-8 rtl:space-x-reverse">
              <a href="#hero" className="text-natural-bg hover:text-natural-highlight transition-colors">
                {getTranslation(translations.home)}
              </a>
              <a href="#books" className="text-natural-bg hover:text-natural-highlight transition-colors">
                {getTranslation(translations.books)}
              </a>
              <a href="#about" className="text-natural-bg hover:text-natural-highlight transition-colors">
                {getTranslation(translations.about)}
              </a>
              <a href="#contact" className="text-natural-bg hover:text-natural-highlight transition-colors">
                {getTranslation(translations.contact)}
              </a>
            </div>
          </div>
          
          {/* Right/Left side (depending on direction) - Language Selector */}
          <div className="flex items-center">
          <div className="hidden md:flex items-center space-x-2 rtl:space-x-reverse">
              <button 
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  currentLanguage === 'en' 
                    ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                    : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                }`}
                onClick={() => changeLanguage('en')}
              >
                EN
              </button>
              <button 
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  currentLanguage === 'fr' 
                    ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                    : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                }`}
                onClick={() => changeLanguage('fr')}
              >
                FR
              </button>
              <button 
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  currentLanguage === 'ar' 
                    ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                    : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                }`}
                onClick={() => changeLanguage('ar')}
              >
                ع
              </button>
            </div>
            
            {/* Mobile Menu Toggle Button */}
            <button 
              className="md:hidden ml-4 rtl:mr-4 rtl:ml-0 text-natural-bg hover:text-natural-highlight"
              onClick={() => {
                const mobileMenu = document.getElementById('mobileMenu');
                if (mobileMenu) {
                  mobileMenu.classList.toggle('hidden');
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu - Improved styling */}
        <div id="mobileMenu" className="hidden md:hidden bg-natural-text/95 w-full">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col space-y-3">
              <a href="#hero" className="text-natural-bg hover:text-natural-highlight transition-colors py-2">
                {getTranslation(translations.home)}
              </a>
              <a href="#books" className="text-natural-bg hover:text-natural-highlight transition-colors py-2">
                {getTranslation(translations.books)}
              </a>
              <a href="#about" className="text-natural-bg hover:text-natural-highlight transition-colors py-2">
                {getTranslation(translations.about)}
              </a>
              <a href="#contact" className="text-natural-bg hover:text-natural-highlight transition-colors py-2">
                {getTranslation(translations.contact)}
              </a>
              
              {/* Language Selector for Mobile - Improved alignment */}
              <div className="flex items-center space-x-2 rtl:space-x-reverse py-2">
                <button 
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    currentLanguage === 'en' 
                      ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                      : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                  }`}
                  onClick={() => changeLanguage('en')}
                >
                  EN
                </button>
                <button 
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    currentLanguage === 'fr' 
                      ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                      : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                  }`}
                  onClick={() => changeLanguage('fr')}
                >
                  FR
                </button>
                <button 
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    currentLanguage === 'ar' 
                      ? 'bg-[#6b7c4c] text-[#f3e7d9]' 
                      : 'bg-[#f3e7d9] text-[#6b7c4c] hover:bg-[#e5d9cb]'
                  }`}
                  onClick={() => changeLanguage('ar')}
                >
                  ع
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Floating Leaves */}
<section id="hero" className="relative overflow-hidden bg-gradient-to-b from-natural-text to-natural-accent text-natural-bg min-h-[calc(100vh-4rem)] md:min-h-0 flex items-center md:block pt-2 pb-4 md:py-24">
  {/* Floating Leaves Background */}
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <div className="leaf-1 absolute w-32 h-32 top-1/4 left-[15%]">
      <svg viewBox="0 0 100 100" fill="currentColor" className="text-natural-bg">
        <path d="M50,0 C60,20 80,40 100,50 C80,60 60,80 50,100 C40,80 20,60 0,50 C20,40 40,20 50,0Z" />
      </svg>
    </div>
    <div className="leaf-2 absolute w-40 h-40 top-1/3 right-[10%]">
      <svg viewBox="0 0 100 100" fill="currentColor" className="text-natural-bg rotate-45">
        <path d="M50,0 C60,20 80,40 100,50 C80,60 60,80 50,100 C40,80 20,60 0,50 C20,40 40,20 50,0Z" />
      </svg>
    </div>
    <div className="leaf-3 absolute w-24 h-24 bottom-1/4 left-[30%]">
      <svg viewBox="0 0 100 100" fill="currentColor" className="text-natural-bg -rotate-12">
        <path d="M50,0 C60,20 80,40 100,50 C80,60 60,80 50,100 C40,80 20,60 0,50 C20,40 40,20 50,0Z" />
      </svg>
    </div>
    <div className="leaf-4 absolute w-36 h-36 top-[15%] right-[25%]">
      <svg viewBox="0 0 100 100" fill="currentColor" className="text-natural-bg rotate-90">
        <path d="M50,0 C60,20 80,40 100,50 C80,60 60,80 50,100 C40,80 20,60 0,50 C20,40 40,20 50,0Z" />
      </svg>
    </div>
    <div className="leaf-5 absolute w-28 h-28 bottom-[20%] right-[35%]">
      <svg viewBox="0 0 100 100" fill="currentColor" className="text-natural-bg rotate-180">
        <path d="M50,0 C60,20 80,40 100,50 C80,60 60,80 50,100 C40,80 20,60 0,50 C20,40 40,20 50,0Z" />
      </svg>
    </div>
  </div>

  {/* Existing Hero Content */}
  <div className="container relative mx-auto px-4 w-full">
    <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-center h-full">
      {/* Book Cover */}
      <div className="flex flex-col items-center justify-center flex-1 w-full">
        <img 
          src="/images/shifaathati_cover.png" 
          alt="Book Cover" 
          className="w-[52%] sm:w-1/2 max-w-[240px] md:max-w-md mx-auto transition-transform duration-500 hover:scale-105"
        />
        <a 
          href="/pdfs/شــفاء ذاتـي.pdf" 
          download="شفاء_ذاتي.pdf" 
          className="my-3 md:mt-6 px-6 py-2.5 md:px-8 md:py-3 text-sm md:text-base bg-natural-highlight text-natural-bg rounded-full hover:bg-natural-highlight/90 transition-colors w-full max-w-[280px] text-center"
        >
          {getTranslation(translations.downloadFullPDF)}
        </a>
      </div>
      
      {/* Video */}
      <div className="w-full mt-2 md:mt-0 pb-2 md:pb-0 flex justify-center">
        <div className="relative overflow-hidden rounded-lg shadow-2xl aspect-video w-[90%] md:w-full max-w-[340px] md:max-w-full">
          <iframe 
            src="https://www.youtube.com/embed/Mvf9u25_9rw?vq=hd1080&rel=0" 
            title="شفاء ذاتي"
            className="absolute top-0 left-0 w-full h-full object-cover"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Image Gallery */}
<section className="py-24 bg-natural-bg">
  <div className="max-w-4xl mx-auto px-4 space-y-10">
    {slides.map((image, index) => (
      <div key={index} className="w-full">
        <img
          src={image}
          alt={`Slide ${index + 1}`}
          className="w-full h-auto rounded-2xl shadow-xl object-contain"
        />
      </div>
    ))}
  </div>
</section>







      {/* Books Section */}
      <section id="books" className="py-20 bg-white">
  <div className="container mx-auto px-4">
    {/* Featured PDF Download Section */}
    <div className="max-w-4xl mx-auto mb-16 bg-[#F3E7DD] rounded-3xl p-8 md:p-12 shadow-lg">
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Left Column - Content */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left h-full justify-center">
          <div className="inline-block px-5 py-2.5 bg-[#6b8e5c] text-white rounded-full text-xs md:text-sm font-bold tracking-wider mb-6">
            {getTranslation({ en: "FREE DOWNLOAD", fr: "TÉLÉCHARGEMENT GRATUIT", ar: "تحميل مجاني" })}
          </div>
          
          <h3 className="text-3xl md:text-4xl font-bold text-[#4a5d3f] mb-4" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
            {getTranslation({ en: "Healing from Within", fr: "شفاء ذاتي", ar: "شفاء ذاتي" })}
          </h3>
          
          <p className="text-[#8b9d83] font-medium mb-10 text-sm md:text-base" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
            {getTranslation(translations.freeBookOffer)}
          </p>
          
          {/* Side-by-side Buttons Container */}
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <a 
              href="/pdfs/شــفاء ذاتـي.pdf" 
              download="شفاء_ذاتي.pdf"
              className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-[#6b8e5c] text-white rounded-full font-bold text-sm md:text-base shadow-md hover:bg-[#5a7b4c] transition-all duration-300 transform hover:-translate-y-1 text-center"
            >
              <svg className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {getTranslation(translations.downloadNow)}
            </a>
            <a 
              href="https://wa.me/21626439062" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-[#4a5d3f] text-white rounded-full font-bold text-sm md:text-base shadow-md hover:bg-[#3b4b32] transition-all duration-300 transform hover:-translate-y-1 text-center"
            >
              {getTranslation(translations.buyNow)}
            </a>
          </div>
        </div>

        {/* Right Column - Image Showcase */}
        <div className="flex justify-center md:justify-end">
          <div className="bg-[#fcf8f5] p-6 md:p-8 rounded-3xl shadow-sm w-[80%] md:w-full max-w-[320px] flex items-center justify-center transition-transform duration-500 hover:scale-[1.02]">
            <img 
              src="/images/shifaathati_cover.png" 
              alt="شفاء ذاتي" 
              className="w-full h-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="flex flex-col items-center mb-16">
      <h2 className="text-3xl font-bold text-center mb-8 text-natural-text">
        {getTranslation(translations.myBooks)}
      </h2>
      
      {/* Filter Dropdown */}
      <div className="relative w-full max-w-xs">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className={`w-full px-4 py-2 bg-natural-bg border border-natural-divider rounded-lg 
            text-natural-text appearance-none cursor-pointer hover:border-natural-accent 
            transition-colors focus:outline-none focus:ring-2 focus:ring-natural-accent/50
            ${currentLanguage === 'ar' ? 'text-right pr-4 pl-10' : 'text-left pl-4 pr-10'}`}
          dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
        >
          <option value="all" className="text-center">
            {getTranslation({ 
              en: "All Books",
              fr: "Tous les livres",
              ar: "كل الكتب"
            })}
          </option>
          <option value="arabic" className="text-center">
            {getTranslation({
              en: "Arabic Books",
              fr: "Livres en arabe",
              ar: "الكتب العربية"
            })}
          </option>
          <option value="french" className="text-center">
            {getTranslation({
              en: "French Books",
              fr: "Livres en français",
              ar: "الكتب الفرنسية"
            })}
          </option>
          <option value="price-asc" className="text-center">
            {getTranslation({
              en: "Price: Low to High",
              fr: "Prix: croissant",
              ar: "السعر: من الأقل إلى الأعلى"
            })}
          </option>
          <option value="price-desc" className="text-center">
            {getTranslation({
              en: "Price: High to Low",
              fr: "Prix: décroissant",
              ar: "السعر: من الأعلى إلى الأقل"
            })}
          </option>
        </select>
        
        {/* Dropdown Arrow */}
        <div className={`absolute inset-y-0 ${currentLanguage === 'ar' ? 'left-0 pl-2' : 'right-0 pr-2'}
          flex items-center pointer-events-none`}>
          <svg 
            className="w-5 h-5 text-natural-text"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </div>
    
    {/* Books Grid - Update the books.map to filteredBooks.map */}
    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-8 place-items-center">
      {filteredBooks.map((book, index) => (
        <div key={index} className="flex flex-col items-center w-full max-w-[280px] min-h-[480px] group">
          {/* Book Image Container - Fixed height */}
          <div className="relative w-full aspect-[3/4] mb-6">
            <div 
              className="w-full h-full transition-all duration-300 
              transform hover:scale-105 group-hover:translate-y-[-10px] flex items-center justify-center cursor-pointer"
              onClick={() => openBookModal(book)}
            >
              <img 
                src={book.image} 
                alt={getTranslation(book.title)} 
                className="w-full h-full object-contain transition-all duration-300"
                style={{
                  isolation: 'isolate',
                  mixBlendMode: 'normal',
                  backgroundColor: 'transparent'
                }}
              />
            </div>
          </div>

          {/* Book Details Container - Fixed height and spacing */}
          <div className="flex flex-col items-center justify-between gap-4 w-full px-4 flex-1">
            {/* Title with modern styling */}
            <h3 className="text-xl font-semibold leading-tight tracking-wide text-natural-text
              min-h-[4.5rem] flex items-center justify-center text-center w-full
              transition-colors duration-300"
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
            >
              {getTranslation(book.title)}
            </h3>
            
            {/* Price and Language - Fixed position at bottom */}
            <div className="flex flex-col items-center gap-3 w-full mt-auto">
              {/* Price with enhanced hover effects */}
              <span 
                className="relative text-3xl font-bold tracking-tight
                  bg-gradient-to-r from-natural-accent to-natural-text bg-clip-text 
                  text-transparent drop-shadow-sm group-hover:drop-shadow-lg
                  transition-all duration-300 transform
                  hover:scale-110 group-hover:from-natural-text group-hover:to-natural-accent"
              >
                {book.price}
              </span>

              {/* Language Tag */}
              <span className="px-4 py-1.5 text-xs font-medium tracking-[0.2em] uppercase
                bg-natural-bg/40 text-natural-secondary/90 rounded-full
                border border-natural-divider/10 shadow-sm backdrop-blur-sm
                transition-all duration-300 hover:bg-natural-bg/60 min-w-[100px]
                flex items-center justify-center"
              >
                {book.language}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-natural-bg/60">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-natural-text">
            {getTranslation(translations.reviews)}
          </h2>
          
          <div className="flex flex-col items-center space-y-8">
            {reviews.map((review, index) => (
              <React.Fragment key={review.id}>
                <div 
                  className="max-w-2xl w-full cursor-pointer transition-transform duration-300 hover:scale-105"
                  onClick={() => openImageModal(review.image)}
                >
                  <img 
                    src={review.image} 
                    alt={`Review ${review.id}`} 
                    className="w-full rounded-lg shadow-md"
                  />
                </div>
                {index < reviews.length - 1 && (
                  <div className="w-1/3 h-1 bg-natural-divider rounded-full"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-xl overflow-hidden border border-natural-divider/20">
            <div className="md:flex">
              <div className="md:w-1/3 bg-gradient-to-b from-natural-text to-natural-accent p-8 text-natural-bg flex flex-col items-center justify-center">
                <div className="w-40 h-40 rounded-full border-4 border-natural-bg overflow-hidden mb-6 shadow-lg">
                  <img src="/images/sondess_image.jpg" alt={getTranslation(translations.authorName)} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{getTranslation(translations.authorName)}</h3>
                <p className="text-natural-bg/80">{getTranslation(translations.authorTitle)}</p>
              </div>
              
              <div className="p-8 md:w-2/3">
                <h2 className="text-2xl font-bold text-natural-text mb-6">
                  {getTranslation(translations.aboutAuthor)}
                </h2>
                <p className="text-natural-secondary mb-8 leading-relaxed">
                  {getTranslation(translations.authorBio)}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a 
                    href="https://www.youtube.com/@Sakina-o1p" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-natural-bg/50 rounded-lg transition-all hover:bg-natural-bg hover:-translate-y-1"
                  >
                    <i className="text-natural-accent mr-2">📻</i>
                    <span className="text-sm text-natural-text">{getTranslation(translations.discoverPodcast)}</span>
                  </a>
                  
                  <a 
                    href="https://youtube.com/@sondess?si=KCEx0xzZqngoUAY5" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-natural-bg/50 rounded-lg transition-all hover:bg-natural-bg hover:-translate-y-1"
                  >
                    <i className="text-natural-accent mr-2">💼</i>
                    <span className="text-sm text-natural-text">{getTranslation(translations.businessCoaching)}</span>
                  </a>
                  
                  <a 
                    href="https://www.youtube.com/@sondesskhalifa7420" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-natural-bg/50 rounded-lg transition-all hover:bg-natural-bg hover:-translate-y-1"
                  >
                    <i className="text-natural-accent mr-2">📺</i>
                    <span className="text-sm text-natural-text">{getTranslation(translations.educationalContent)}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          

          {/* Coaching Request */}
          <div className="mt-8 max-w-2xl mx-auto bg-natural-bg/70 rounded-xl p-8 text-center shadow-lg border border-natural-divider/20">
            <h3 className="text-xl font-bold mb-4 text-natural-text">
              {getTranslation(translations.needCoaching)}
            </h3>
            <a 
              href="https://wa.me/21626439062" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 bg-natural-accent text-natural-bg rounded-lg shadow-md hover:bg-natural-accent/90 transition-colors"
            >
              {getTranslation(translations.requestCoaching)}
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-natural-text to-natural-accent text-natural-bg text-center">
        <div className="container mx-auto px-4">
          <a 
            href="https://wa.me/21626439062" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-natural-highlight text-natural-bg rounded-full text-lg font-bold shadow-xl hover:bg-natural-highlight/90 transition-colors"
          >
            {getTranslation(translations.buyNow)}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-natural-text text-natural-bg py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p>{getTranslation(translations.footerText)}</p>
            <p className="mt-4 md:mt-0">{getTranslation(translations.footerCredit)}</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <a 
        href="https://wa.me/21626439062" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-natural-accent rounded-full flex items-center justify-center text-natural-bg text-2xl shadow-lg hover:bg-natural-accent/90 transition-colors z-50"
        aria-label="WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
      </a>

      {/* Image Modal (Avis des lecteurs) */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 w-full max-w-5xl translate-x-[-50%] translate-y-[-50%] border-none bg-transparent p-4 md:p-8 shadow-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] outline-none flex items-center justify-center"
            )}
            onClick={() => setShowImageModal(false)}
          >
            {/* Custom Close Button for Image Modal */}
            <DialogPrimitive.Close 
              className="absolute right-4 top-4 md:right-8 md:top-8 rounded-full p-2 bg-black/50 text-white hover:bg-black/70 transition-colors z-[60] focus:outline-none ring-2 ring-white/20 hover:ring-white/50"
              onClick={(e) => e.stopPropagation()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              <span className="sr-only">Close review image</span>
            </DialogPrimitive.Close>
            
            <div 
              className="relative w-full h-full max-h-[85vh] flex items-center justify-center" 
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={modalImage} 
                alt="Review" 
                className="max-w-full max-h-full object-contain rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.5)] bg-white/5"
              />
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Book Modal */}
      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 w-[95%] max-w-4xl max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] bg-[#fdfbf9] border border-natural-accent/10 rounded-3xl p-0 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
            )}
          >
            {/* Custom Close Button */}
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full p-2 hover:bg-natural-divider/50 transition-colors z-[60] text-natural-text opacity-70 hover:opacity-100 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>

            {selectedBook && (
              <div className="grid md:grid-cols-2 h-full">
                {/* Left Column - Book Cover with Soft Background */}
                <div className="relative w-full flex items-center justify-center bg-[#f3e7dd]/50 p-8 md:p-12 min-h-[300px] md:min-h-[400px]">
                  <img 
                    src={selectedBook.image} 
                    alt={getTranslation(selectedBook.title)} 
                    className="w-full max-w-[200px] sm:max-w-xs md:max-w-sm h-auto object-contain drop-shadow-2xl transition-transform duration-500 hover:scale-105"
                  />
                </div>
                
                {/* Right Column - Info & Actions */}
                <div className="flex flex-col justify-center gap-6 p-8 md:p-12">
                  <div className="space-y-4 text-center md:text-left">
                    {/* Language Tag */}
                    <span className="inline-block px-4 py-1.5 text-xs font-bold tracking-wider uppercase bg-natural-bg text-natural-secondary border border-natural-divider/50 rounded-full shadow-sm">
                      {selectedBook.language}
                    </span>

                    {/* Book Title */}
                    <h2 
                      className="text-3xl md:text-4xl font-bold text-[#4a5d3f] leading-tight"
                      dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                    >
                      {getTranslation(selectedBook.title)}
                    </h2>
                    
                    {/* Book Description (Optional - using subtitle if available, else static text) */}
                    <p className="text-[#8b9d83] leading-relaxed text-sm md:text-base" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
                      {getTranslation(selectedBook.title) === 'شفاء ذاتي' || getTranslation(selectedBook.title) === 'Healing from Within' 
                        ? getTranslation(translations.freeBookOffer) 
                        : "Découvrez notre collection de livres inspirants pour votre développement personnel et votre bien-être."}
                    </p>
                  </div>
                  
                  {/* Price Section */}
                  <div className="flex items-center justify-center md:justify-start gap-2 pt-4 border-t border-natural-divider/30">
                    <span className="text-3xl md:text-4xl font-extrabold text-natural-text">
                      {selectedBook.price}
                    </span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full justify-center md:justify-start">
                    {(selectedBook.price === 'Gratuit' || selectedBook.price === 'Free' || selectedBook.price === 'مجاني') ? (
                      <a 
                        href="/pdfs/شــفاء ذاتـي.pdf" 
                        download="شفاء_ذاتي.pdf"
                        className="inline-flex items-center justify-center w-full md:w-auto px-8 py-3.5 bg-[#6b8e5c] text-white rounded-full font-bold shadow-md hover:bg-[#5a7b4c] transition-all duration-300 transform hover:-translate-y-1"
                      >
                        <svg className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {getTranslation(translations.downloadNow)}
                      </a>
                    ) : (
                      <a 
                        href={`https://wa.me/21626439062?text=${encodeURIComponent("Bonjour, je souhaite acheter le livre: " + getTranslation(selectedBook.title))}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full md:w-auto px-8 py-3.5 bg-[#4a5d3f] text-white rounded-full font-bold shadow-md hover:bg-[#3b4b32] transition-all duration-300 transform hover:-translate-y-1"
                      >
                       {getTranslation(translations.buyNow)}
                      </a>
                    )}
                  </div>

                </div>
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Coming Soon Popup */}
      <Dialog>
        <DialogContent className="bg-natural-bg border-natural-divider">
          <DialogHeader>
            <DialogTitle className="text-natural-text">{getTranslation(translations.comingSoon)}</DialogTitle>
            <DialogDescription className="text-natural-secondary">
              This feature will be available soon!
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
};

export default ChifaathatiWebsite;

